import { useQuery, useReactiveVar } from "@apollo/client";
import { PlusIcon } from "@heroicons/react/24/outline";
import { currentDistrictVar } from "apollo/cache/auth";
import {
  HeaderTabs,
  OfficeCalendarHeader,
  PremiumServiceInterestGridCard,
  Shimmers,
  TableComponent,
} from "components";
import config from "config";
import { FC, useEffect, useMemo } from "react";
import { useNavigate, useSearch } from "react-location";
import { LocationGenerics } from "router/location";
import {
  closeModal,
  dispatchModal,
  useTableData,
  useUrlState,
  wrapClick,
} from "utils";
import {
  GET_PREMIUM_SERVICE_INTERESTS,
  GET_PREMIUM_SERVICE_INTERESTS_SUMMARY,
} from "./apollo";
import CreatePremiumServiceInterestContainer from "./create";
import CancelPremiumServiceInterestContainer from "./cancel";
import ViewPremiumServiceInterestContainer from "./view";
import ConfirmPremiumServiceInterestContainer from "./confirm";


const mainTabs = [
  { name: "Pending", href: "Pending" },
  {
    name: "Appointment Confirmed",
    href: "AppointmentConfirmed",
  },
];

const altTabs = [
  { name: "Cancelled", href: "Cancelled" },
]


const PremiumServiceInterestsPage: FC = () => {
  const searchParams = useSearch<LocationGenerics>();
  const navigate = useNavigate<LocationGenerics>();
  const [modal, setModal] = useUrlState("modal");
  const currentDistrict = useReactiveVar(currentDistrictVar);

  const filter = useMemo(
    () => ({
      page: Number(searchParams.page || config.constants.page),
      pageSize: Number(searchParams.pageSize || config.constants.pageSize),
      search: (searchParams.search || "").toString(),
      searchFields: [
        "code",
        "customer.fullName",
        "customer.phoneNumber",
        "customer.digitalAddress",
      ],
      ...(currentDistrict ? { district: currentDistrict } : {}),
      status: searchParams.orderStatus || undefined,
      sort: "-createdAt",
      fromDate: searchParams.fromDate || undefined,
      toDate: searchParams.toDate || undefined,
    }),
    [searchParams, currentDistrict],
  );

  const { data, loading, networkStatus, refetch } = useQuery(
    GET_PREMIUM_SERVICE_INTERESTS,
    {
      variables: filter,
      notifyOnNetworkStatusChange: true,
      onCompleted: () => {
        refetchSummary?.();
      },
    },
  );

  const { data: dataSummary, refetch: refetchSummary } = useQuery(
    GET_PREMIUM_SERVICE_INTERESTS_SUMMARY,
    {
      variables: filter,
      notifyOnNetworkStatusChange: false,
    },
  );

  useEffect(() => {
    navigate({
      search: (old) => ({
        ...old,
        ...(!old?.page ? { page: config.constants.page } : {}),
        ...(!old?.pageSize ? { pageSize: config.constants.pageSize } : {}),
        ...(!old?.search ? { search: "" } : {}),
      }),
    });
  }, [navigate]);

  useEffect(() => {
    navigate({
      search: (old) => ({
        ...old,
        id: undefined,
      }),
    });
  }, [searchParams.orderStatus, navigate]);

  const records = useTableData(data || {});

  return (
    <main className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      <OfficeCalendarHeader
        defaultView="month"
        renderActions={() => (
          <button
            type="button"
            onClick={wrapClick(() => setModal("create"))}
            className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm leading-4 text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" aria-hidden="true" />
            Create Expression of Interest
          </button>
        )}
      />
      <HeaderTabs
        mainTabs={mainTabs}
        altTabs={altTabs}
        tabKey="orderStatus"
        summary={dataSummary?.summary}
      />
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 min-h-full mx-auto min-w-0 py-4 sm:py-6 overflow-hidden overflow-y-auto light flex">
          <TableComponent
            title="expressions of interest"
            refetch={refetch}
            isRefetching={loading && networkStatus === 4}
            loading={loading && ![4, 6].includes(networkStatus)}
            data={records}
            hasSearch={true}
            defaultView="grid"
            renderGridLoader={() => (
              <div className="overflow-hidden rounded-lg bg-white dark:bg-gray-900 shadow">
                <div className="flex pb-8 px-4 pt-4">
                  <div className="rounded-md h-12 w-12 bg-gray-400 animate-pulse" />
                  <dd className="ml-4 flex-1 flex items-center">
                    <Shimmers.DoubleShimmer />
                  </dd>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 px-3 py-1.5 flex justify-between sm:px-4 space-x-10">
                  <Shimmers.SingleShimmer />
                  <Shimmers.ActionsShimmer actionsCount={3} />
                </div>
              </div>
            )}
            renderGridItem={(item: any) => (
              <PremiumServiceInterestGridCard
                item={item}
                onView={dispatchModal(item._id, "view")}
                onCancel={dispatchModal(item._id, "cancel")}
                onSchedule={dispatchModal(item._id, "schedule")}
              />
            )}
          />
        </div>
      </div>
      <CreatePremiumServiceInterestContainer
        open={modal === "create"}
        setOpen={(val) => setModal(val ? "create" : undefined)}
        refetch={refetch}
      />

      {!!searchParams.id?.length && (
        <>
          <ViewPremiumServiceInterestContainer
            open={modal === "view"}
            setOpen={closeModal}
          />
          <CancelPremiumServiceInterestContainer
            open={modal === "cancel"}
            setOpen={closeModal}
            refetch={refetch}
          />
          <ConfirmPremiumServiceInterestContainer
            open={modal === "schedule"}
            setOpen={closeModal}
            refetch={refetch}
          />
        </>
      )}
    </main>
  );
};

export default PremiumServiceInterestsPage;
